/* eslint-disable react/prop-types */
import { Circle, geometry, Group, Text } from '@progress/kendo-drawing';
import { LegendItemVisualArgs } from '@progress/kendo-react-charts';
import { TFunction } from 'i18next';
import moment from 'moment';
import { dateAdd, formatDate } from '../../../../../common/global/helpers';
import { resources } from '../../../resources/resources';
import { ChartPeriodModel, PeriodModel } from './types';

// return a string on multiple lines, without changing a line in the middle of a word
export const formatLabel = (text: string, maxRowLength: number): string => {
  const words = text.split(' ');
  const rows: { [k: string]: string[] } = {};
  let currentRow = 0;
  let currRowLength = 0;

  for (const word of words) {
    if (currRowLength >= maxRowLength) {
      currentRow += 1;
      currRowLength = 0;
    }
    if (rows[`${currentRow}`]) {
      rows[`${currentRow}`].push(word);
      currRowLength += word.length;
    } else {
      rows[`${currentRow}`] = [word];
      currRowLength += word.length;
    }
  }

  return Object.values(rows).reduce((prev, curr) => {
    const currentRow = curr.reduce((p, c) => `${p} ${c}`, '');
    return `${prev} ${currentRow}\n`;
  }, '');
};

export const mapPeriod = (p: ChartPeriodModel): PeriodModel => ({
  text: p.text,
  startDate: p.startDate ?? new Date(),
  endDate: p.endDate ?? new Date(),
});

export const getChartPeriods: (
  translate: TFunction,
) => ChartPeriodModel[] = translate => {
  const now = moment();
  const lastMonth = moment().add({ months: -1 });
  const sixMonthsAgo = moment().add({ months: -6 });
  const lastYear = moment().add({ years: -1 });
  return [
    {
      text: translate(resources.analytics.periodPicker.currentYear),
      startDate: new Date(now.year(), 0, 1),
      endDate: new Date(),
    },
    {
      text: translate(resources.analytics.periodPicker.currentMonth),
      startDate: new Date(now.year(), now.month(), 1),
      endDate: new Date(),
    },
    {
      text: translate(resources.analytics.periodPicker.lastMonth),
      startDate: new Date(lastMonth.year(), lastMonth.month(), 1),
      endDate: new Date(
        lastMonth.year(),
        lastMonth.month(),
        lastMonth.daysInMonth(),
      ),
    },
    {
      text: translate(resources.analytics.periodPicker.last6Months),
      startDate: new Date(sixMonthsAgo as any),
      endDate: new Date(),
    },
    {
      text: translate(resources.analytics.periodPicker.lastYear),
      startDate: new Date(lastYear.year(), 0, 1),
      endDate: new Date(lastYear.year(), 11, 31),
    },
    {
      text: translate(resources.analytics.periodPicker.custom),
      isCustom: true,
    },
  ];
};

export const seriesPeriods = {
  current: 'current',
  previous: 'previous',
};

export const seriesColor = '#ff220d';
export const markerTooltipColor = '#555555';

export const getPreviousPeriod = (period: PeriodModel): PeriodModel => {
  return {
    ...period,
    startDate: dateAdd(period.startDate, { years: -1 }),
    endDate: dateAdd(period.endDate, { years: -1 }),
  };
};

const { Point, Circle: GeomCircle } = geometry;

export const getLegendItemVisual = (
  props: LegendItemVisualArgs,
  period: PeriodModel,
) => {
  const isCurrent = props.series.name === seriesPeriods.current;

  const geometry = new GeomCircle([0, 0], 5);

  const circle = new Circle(geometry, {
    stroke: { color: seriesColor, width: 2 },
    fill: { color: seriesColor },
    opacity: isCurrent ? 1 : 0.5,
  });

  const p = isCurrent ? period : getPreviousPeriod(period);

  const labelText = `${formatDate(
    p.startDate.toDateString(),
    'DD.MM.YYYY',
  )} - ${formatDate(p.endDate.toDateString(), 'DD.MM.YYYY')}`;

  const labelFont = props.options.labels.font;
  const fontColor = props.options.labels.color;
  const textOptions = {
    font: labelFont,
    fill: { color: fontColor },
  };
  const text = new Text(labelText, new Point(12, -8), textOptions);
  const group = new Group();

  group.append(circle, text);

  return group;
};

export const markerTooltipStyle: any = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
};
